<template>
    <div id="triggers">
        <AppBreadCrumb :paths="[{ title: $t('message.triggers') }]" :select="true">
            <a-button
                class="actionButtonBreadcrumb"
                @click="showModal"
                v-if="checkPermission(PERMISSION_TYPES['trigers.create'].tr.name)"
            >
                <md-icon>add</md-icon>
                {{ $t("message.addTrigger") }}
            </a-button>
            <div
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                :content="!isEnabledTriggers ? $t('message.showTriggers') : $t('message.hideTriggers')"
                style="margin: 0px 10px 3px"
            >
                <a-switch v-model="isEnabledTriggers" size="small">
                    <a-icon slot="checkedChildren" type="check" />
                    <a-icon slot="unCheckedChildren" type="close" />
                </a-switch>

                <span>{{ $t("message.triggers") }}</span>
            </div>
            <div
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                :content="!triggerSkipLoginForm ? $t('message.triggerSkipLoginFormTrue') : $t('message.triggerSkipLoginFormFalse')"
                style="margin: 0px 10px 3px"
            >
                <a-switch v-model="triggerSkipLoginForm" size="small">
                    <a-icon slot="checkedChildren" type="check" />
                    <a-icon slot="unCheckedChildren" type="close" />
                </a-switch>
                <span>{{ $t("message.triggerSkipLoginForm") }}</span>
            </div>
        </AppBreadCrumb>
        <div v-if="skeletonLoading">
            <b-row>
                <b-col class="skeletons">
                    <b-skeleton-img />
                </b-col>
                <b-col class="skeletons">
                    <b-skeleton-img />
                </b-col>
                <b-col class="skeletons">
                    <b-skeleton-img />
                </b-col>
            </b-row>
        </div>
        <b-container class="m-3" fluid v-else>
            <div
                v-if="!!trigger && trigger.length <= 0"
                class="card py-3"
                style="max-width: 700px; width: 100%; margin: 0 auto"
            >
                <div class="text-center">
                    <a-icon type="info-circle" style="font-size: 30px; color: rgb(246 214 65)" />
                    <h3 class="text-center">{{ $t("message.noTrigger") }}</h3>
                </div>
                <p class="trigger-recipe">
                    <span>{{ $t("message.toCreate") }}</span>
                    <span>
                        <a-button
                            class="actionButtonBreadcrumb mx-1"
                            @click="showModal"
                            v-if="checkPermission(PERMISSION_TYPES['trigers.create'].tr.name)"
                        >

                            <md-icon>add</md-icon>
                            {{ $t("message.addTrigger") }}
                        </a-button></span
                    >
                    <span>{{ $t("message.clickTheButton") }}</span>
                </p>
            </div>
            <draggable class="row trigger-labels" v-model="triggerOrdered" :key="'trigger_' + renderCount" v-else>
                <b-col
                    md="4"
                    v-for="item in triggerOrdered"
                    :key="item._id"
                    :id="item._id"
                    class="trigger-card m-3 d-flex flex-column justify-content-between"
                >
                    <div class="header">
                        {{ !!item.title ? item.title : $t("message.noTitle") }}
                    </div>
                    <div
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="($_.get(item, 'message.0.content', '') || '').replace(/\<\/?.*?\>/gi, '\n')"
                        class="trigger-content"
                    >
                        {{ clearHtml(item).length > 40 ? clearHtml(item).substring(0, 38) + "..." : clearHtml(item) }}
                    </div>
                    <div class="content">
                        <div class="content-item" v-for="(c, i) in item.conditions" :key="i">
                            <div class="title">{{ $t("message." + c.condition) }}</div>
                            <div class="title">{{ $t("message." + c.operator).toLowerCase() }}:</div>
                            <div class="description">{{ c.value }}</div>
                        </div>
                    </div>
                    <div class="d-flex w-100 justify-content-center">
                        <a-button
                            class="actionButton d-flex align-items-center mr-1"
                            @click="edit(item)"
                            v-if="checkPermission(PERMISSION_TYPES['trigers.update'].tr.name)"
                        >
                            <span style="margin-right: 16px; display: block">{{ $t("message.edit") }}</span>
                            <ActionEdit />
                        </a-button>
                        <a-button
                            class="actionButton d-flex align-items-center"
                            @click="remove(item._id)"
                            v-if="checkPermission(PERMISSION_TYPES['trigers.delete'].tr.name)"
                        >
                            <span style="margin-right: 16px; display: block">{{ $t("message.deleteField") }}</span>
                            <ActionDelete />
                        </a-button>
                        <a-button
                            class="actionButton d-flex align-items-center"
                            @click="playPreview(item)"
                            style="width: inherit"
                        >
                            <span
                                v-if="!!selectedTrigger && selectedTrigger._id === item._id"
                                class="mx-0 px-0"
                                style="display: block"
                                >Pause
                                <a-icon type="pause-circle" style="margin-left: 5px" />
                            </span>
                            <span v-else class="mx-0 px-0" style="display: block">
                                Play
                                <a-icon type="play-circle" style="margin-left: 5px" />
                            </span>
                        </a-button>
                        <span
                            v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                            :content="!item.enabled ? $t('message.showThisTrigger') : $t('message.hideThisTrigger')"
                            class="separate-trigger-switch"
                        >
                            <a-switch @change="updateTrigger(item)" v-model="item.enabled" size="small">
                                <a-icon slot="checkedChildren" type="check" />
                                <a-icon slot="unCheckedChildren" type="close" />
                            </a-switch>

                            <span style="margin-left: 5px">{{
                                item.enabled ? $t("message.on") : $t("message.off")
                            }}</span>
                        </span>
                    </div>
                </b-col>
            </draggable>
        </b-container>
        <div
            class="fab d-flex justify-content-center align-items-center"
            @click="showModalWithoutEdit"
            v-if="checkPermission(PERMISSION_TYPES['trigers.create'].tr.name)"
        >
            <md-icon class="">add</md-icon>
        </div>
        <Modal2
            v-if="isModalShow"
            id="trigger-form"
            :title="editMode ? $t('message.updateTrigger') : $t('message.addTrigger')"
            :show-footer="false"
            size="lg"
            @cancel="hideModal"
        >
            <div id="modal-inner">
                <b-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                    <b-row>
                        <b-col md="12">
                            <b-form-group :label="$t('message.title')">
                                <b-form-input v-model="form.title" />
                            </b-form-group>
                            <b-checkbox-group
                                v-model="form.options"
                                :options="options"
                                class="d-flex flex-column my-3"
                            />
                            <div class="d-flex flex-column my-3">
                                <b-checkbox type="checkbox" id="headerCheckbox" v-model="form.showHeaderImage">
                                    <label for="headerCheckbox">{{ $t("message.showHeaderImage") }}</label>
                                </b-checkbox>
                                <b-checkbox type="checkbox" id="popupCheckbox" v-model="form.popupTireggers">
                                    <label for="popupCheckbox">Ekranın Ortasında Göster</label>
                                </b-checkbox>
                            </div>
                            <div class="row" v-if="false">
                                <!-- bu kısım migration sonrasında kaldırılacak-->
                                <a-input
                                    class="col mx-3 py-1 px-2"
                                    :placeholder="$t('message.yesPlease')"
                                    v-model="form.firstAnswer"
                                    type="text"
                                />
                                <a-input
                                    class="col mx-3 py-1 px-2"
                                    :placeholder="$t('message.noThanks')"
                                    v-model="form.secondAnswer"
                                    type="text"
                                />
                            </div>
                            <div class="row d-flex" v-if="form.options.includes('ask')">
                                <MultiContent
                                    :hideTextCustomization="true"
                                    class="col mx-3 py-1 px-2 w-25"
                                    v-model="multiFirstAnswerComp"
                                />

                                <MultiContent
                                    :hideTextCustomization="true"
                                    class="col mx-3 py-1 px-2 w-25"
                                    v-model="multiSecondAnswerComp"
                                />
                            </div>
                            <MultiContent ref="MultiContent" v-model="multiContentComp" />
                            <b-form-group :label="$t('message.messageDurationPerRow')">
                                <a-slider :max="60" :min="0" :step="1" v-model="form.messageDuration" />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <h4 class="my-2">{{ $t("message.conditions") }}</h4>
                    <b-row v-for="(c, i) in form.conditions" class="mb-3" :key="i">
                        <b-col md="4">
                            <b-form-select v-model="c.condition">
                                <option v-for="condKey in Object.keys(conditions || {})" :value="condKey">
                                    {{ $t("message." + condKey) }}
                                </option>
                            </b-form-select>
                        </b-col>
                        <b-col md="4">
                            <b-form-select v-model="c.operator">
                                <option
                                    v-for="operator in operators[conditions[c.condition].operator]"
                                    :value="operator"
                                >
                                    {{ $t("message." + operator) }}
                                </option>
                            </b-form-select>
                        </b-col>
                        <b-col md="3">
                            <b-form-select v-model="c.value" v-if="$_.isArray(conditions[c.condition].options)">
                                <option
                                    v-for="option in conditions[c.condition].options"
                                    :value="$_.isObject(option) ? option.value : option"
                                >
                                    {{ $_.isObject(option) ? option.label : option }}
                                </option>
                            </b-form-select>
                            <b-form-select v-model="c.value" v-else-if="!!conditions[c.condition].options">
                                <option
                                    v-for="option in getState(conditions[c.condition].options)"
                                    :value="option.value"
                                >
                                    {{ option.label }}
                                </option>
                            </b-form-select>
                            <b-form-input v-else v-model="c.value" />
                        </b-col>
                        <b-col md="1">
                            <div @click="delCondition(i)" style="cursor: pointer">
                                <md-icon class="text-danger">remove</md-icon>
                            </div>
                        </b-col>
                    </b-row>
                    <p
                        id="trigger-no-condition-text"
                        style="background: yellow"
                        class="text-center m-1 p-2"
                        v-if="!form.conditions.length"
                    >
                        {{ $t("message.noCondition") }}
                    </p>
                    <b-button variant="primary" size="sm" @click="addCondition" class="mb-5 mt-2" style="width: 50px">
                        <md-icon class="text-white">add</md-icon>
                    </b-button>
                    <b-row>
                        <b-col md="12" id="buttons">
                            <b-button @click="hideModal">
                                <template>
                                    {{ $t("message.cancel") }}
                                </template>
                            </b-button>
                            <b-button :disabled="loading" type="submit" variant="primary">
                                <template v-if="!loading">
                                    <span v-if="!editMode">
                                        {{ $t("message.save") }}
                                    </span>
                                    <span v-else>
                                        {{ $t("message.update") }}
                                    </span>
                                </template>
                                <template v-else>
                                    <font-awesome-icon icon="spinner" spin />
                                </template>
                            </b-button>
                        </b-col>
                    </b-row>
                </b-form>
            </div>
        </Modal2>
        <ChatBar v-if="!isAdmin" :visible="false" />
        <TriggerPreview :trigger="selectedTrigger" v-if="!!selectedTrigger" @trigger-end="selectedTrigger = $event" />
    </div>
</template>

<script>
import AppBreadCrumb from "../../components/common/AppBreadCrumb";
import ChatBar from "../../components/ChatBar";
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import ActionEdit from "../../components/svg/ActionEdit";
import ActionDelete from "../../components/svg/ActionDelete";
import draggable from "vuedraggable";
import MultiContent from "../../components/MultiContent";
import _ from "lodash";
import { sendSocketMessage } from "../../utils/socketStream";
import BubblePreview from "../../components/fast-initialize/BubblePreview";
import TriggerPreview from "../../components/trigger/TriggerPreview";
import log2 from "../../utils/log2";
import Modal2 from "../../components/common/Modal2.vue";
import { PERMISSION_TYPES } from "../../language/permissionTypes";

const log = log2("Triggers");
library.add(faSpinner);
export default {
    name: "Triggers",
    components: {
        BubblePreview,
        TriggerPreview,
        MultiContent,
        AppBreadCrumb,
        ChatBar,
        ActionEdit,
        ActionDelete,
        draggable,
        Modal2,
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            isAdmin: (state) => state.auth.adminUser != null,
            languages: (state) => state.language.languages,
            subjects: (state) =>
                state.subject.subjects.data.map((x) => ({ value: x._id, label: x.languageContent.tr.name })),
            trigger: (state) => state.trigger.trigger,
            siteId: (state) => state.site.site._id,
            settings: (state) => state.setting.settings,
            skeletonLoading: (state) => state.trigger.skeletonLoading,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        multiContentComp: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                return [
                    {
                        type: "rich",
                        field: "content",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.form.message, `[${lang}]`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.form.message = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
        multiFirstAnswerComp: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                return [
                    {
                        type: "rich",
                        field: "content",
                        label: this.$t("message.firstAnswer"),
                        placeholder: this.$t("message.yesPlease"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.form.confirmAskingDialog, `[${lang}]`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.form.confirmAskingDialog = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
        multiSecondAnswerComp: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                return [
                    {
                        type: "rich",
                        field: "content",
                        label: this.$t("message.secondAnswer"),
                        placeholder: this.$t("message.noThanks"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.form.dismissAskingDialog, `[${lang}]`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.form.dismissAskingDialog = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
        triggerOrdered: {
            get() {
                return this.trigger;
            },
            set(e) {
                let orderUpdated = _.clone(e).map((x, i) => ({ ...x, order: i }));
                let diffs = _.differenceWith(orderUpdated, this.trigger, _.isEqual);
                diffs.forEach((x) => {
                    this.updateTrigger(x);
                    this.renderCount++;
                    this.hideModal();
                });
            },
        },
        isEnabledTriggers: {
            get() {
                return this.settings.isEnabledTriggers;
            },
            set(value) {
                const newSettings = this.setSettingValue({
                    setting: "isEnabledTriggers",
                    value: value,
                });

                if (newSettings.then) {
                    newSettings.then((x) => this.handleSaveSettings(x));
                } else {
                    this.handleSaveSettings(newSettings);
                }
            },
        },
        triggerSkipLoginForm: {
            get() {
                return this.settings.triggerSkipLoginForm;
            },
            set(value) {
                const newSettings = this.setSettingValue({
                    setting: "triggerSkipLoginForm",
                    value: value,
                });

                if (newSettings.then) {
                    newSettings.then((x) => this.handleSaveSettings(x));
                } else {
                    this.handleSaveSettings(newSettings);
                }
            },
        },
    },
    data() {
        return {
            selectedTrigger: null,
            triggerInfo: {
                beforeUpdated: null,
                afterUpdated: null,
            },
            previewedList: [],
            add: "",
            renderCount: 0,
            loading: false,
            editMode: false,
            showPreview: false,
            tempTrigger: null,
            cols: [
                {
                    dataIndex: "title",
                    title: this.$t("message.title"),
                },
                {
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                    width: "10%",
                },
            ],
            form: {
                title: "",
                message: {},
                messageDuration: 0,
                conditions: [],
                order: null,
                options: [],
                firstAnswer: "",
                secondAnswer: "",
                showHeaderImage: false,
                popupTireggers: false,
                confirmAskingDialog: {},
                dismissAskingDialog: {},
            },
            conditions: {
                dayTime: {
                    operator: "compare",
                    options: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
                },
                weekDay: {
                    operator: "compare",
                    options: [
                        {
                            label: this.$t("message.monday"),
                            value: "monday",
                        },
                        {
                            label: this.$t("message.tuesday"),
                            value: "tuesday",
                        },
                        {
                            label: this.$t("message.wednesday"),
                            value: "wednesday",
                        },
                        {
                            label: this.$t("message.thursday"),
                            value: "thursday",
                        },
                        {
                            label: this.$t("message.friday"),
                            value: "friday",
                        },
                        {
                            label: this.$t("message.saturday"),
                            value: "saturday",
                        },
                        {
                            label: this.$t("message.sunday"),
                            value: "sunday",
                        },
                    ],
                },
                spendTimePage: {
                    operator: "compare",
                    options: [
                        {
                            label: this.$t("message.seconds10"),
                            value: 10,
                        },
                        {
                            label: this.$t("message.seconds30"),
                            value: 30,
                        },
                        {
                            label: this.$t("message.minute1"),
                            value: 60,
                        },
                        {
                            label: this.$t("message.minutes5"),
                            value: 300,
                        },
                        {
                            label: this.$t("message.minutes10"),
                            value: 600,
                        },
                        {
                            label: this.$t("message.minutes30"),
                            value: 1800,
                        },
                        {
                            label: this.$t("message.hour1"),
                            value: 3600,
                        },
                        {
                            label: this.$t("message.hours2"),
                            value: 7200,
                        },
                        {
                            label: this.$t("message.hours4"),
                            value: 14400,
                        },
                        {
                            label: this.$t("message.hours8"),
                            value: 28800,
                        },
                        {
                            label: this.$t("message.hours12"),
                            value: 43200,
                        },
                        {
                            label: this.$t("message.hours24"),
                            value: 86400,
                        },
                    ],
                },
                spendTimeSite: {
                    operator: "compare",
                    options: [
                        {
                            label: this.$t("message.seconds10"),
                            value: 10,
                        },
                        {
                            label: this.$t("message.seconds30"),
                            value: 30,
                        },
                        {
                            label: this.$t("message.minute1"),
                            value: 60,
                        },
                        {
                            label: this.$t("message.minutes5"),
                            value: 300,
                        },
                        {
                            label: this.$t("message.minutes10"),
                            value: 600,
                        },
                        {
                            label: this.$t("message.minutes30"),
                            value: 1800,
                        },
                        {
                            label: this.$t("message.hour1"),
                            value: 3600,
                        },
                        {
                            label: this.$t("message.hours2"),
                            value: 7200,
                        },
                        {
                            label: this.$t("message.hours4"),
                            value: 14400,
                        },
                        {
                            label: this.$t("message.hours8"),
                            value: 28800,
                        },
                        {
                            label: this.$t("message.hours12"),
                            value: 43200,
                        },
                        {
                            label: this.$t("message.hours24"),
                            value: 86400,
                        },
                    ],
                },
                pageUrl: {
                    operator: "string_compare",
                    options: null,
                },
                pageTitle: {
                    operator: "string_compare",
                    options: null,
                },
                systemStatus: {
                    operator: "basic_compare",
                    options: [
                        {
                            label: this.$t("message.online"),
                            value: true,
                        },
                        {
                            label: this.$t("message.offline"),
                            value: false,
                        },
                    ],
                },
                visitorName: {
                    operator: "string_compare",
                    options: null,
                },
                department: {
                    operator: "basic_compare",
                    options: "subjects",
                },
            },
            operators: {
                compare: [
                    "equal",
                    "not_equal",
                    "less_than",
                    "greater_than",
                    "less_than_or_equal",
                    "greater_than_or_equal",
                ],
                string_compare: ["equal", "not_equal", "contains", "not_contains"],
                basic_compare: ["equal", "not_equal"],
            },
            editId: null,
            options: [
                { text: this.$t("message.openBubble"), value: "open" },
                { text: this.$t("message.askQuestion"), value: "ask" },
            ],
            isModalShow: false,
            queryAction: this.$route.query.action,
        };
    },
    methods: {
        ...mapActions({
            addTrigger: "trigger/addTrigger",
            getTrigger: "trigger/getTrigger",
            updateTrigger: "trigger/updateTrigger",
            deleteTrigger: "trigger/deleteTrigger",
            updateSettings: "setting/updateSettings",
            setSettingValue: "setting/setSettingValue",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        showModalWithoutEdit() {
            this.isModalShow = true;
            this.editMode = false;
        },
        handleSaveSettings(newSettings) {
            const self = this;
            return this.$validator.validateAll().then((response) => {
                if (response) {
                    self.updateSettings(newSettings)
                        .then(() => {
                            sendSocketMessage("admin settings-updated", {
                                siteId: self.siteId,
                            });
                            self.$toasted.show(self.$t("message.settingsSaved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .finally(() => {
                            self.submitClicked = false;
                        });
                } else {
                    self.$toasted.error(self.$t("message.settingFormWrong"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                    this.loading = false;
                }
            });
        },
        reset() {
            this.form = {
                title: "",
                message: {
                    en: "",
                    tr: "",
                    ru: "",
                    de: "",
                    fr: "",
                    es: "",
                    ar: "",
                    it: "",
                },
                messageDuration: 0,
                conditions: [
                    {
                        condition: "dayTime",
                        operator: "equal",
                        value: "0",
                    },
                ],
                options: [],
                firstAnswer: "",
                secondAnswer: "",
                confirmAskingDialog: {
                    en: "",
                    tr: "",
                    ru: "",
                    de: "",
                    fr: "",
                    es: "",
                    ar: "",
                    it: "",
                },
                dismissAskingDialog: {
                    en: "",
                    tr: "",
                    ru: "",
                    de: "",
                    fr: "",
                    es: "",
                    ar: "",
                    it: "",
                },
            };
            this.editMode = false;
            this.editId = null;
        },
        async handleSave() {
            this.loading = true;
            try {
                if (!this.form.title) {
                    this.$message.error(this.$t("message.requiredTitle"));
                    this.loading = false;
                    return;
                }
                // const availableLanguages = this.languages.map((x) => x.code);
                // for (const lang of availableLanguages) {
                //     if (!this.form.message[lang]) {
                //         this.$message.error(`Language (${lang.toUpperCase()}) cannot be empty.`);
                //         this.loading = false;
                //         return;
                //     }
                // }
                let data = this.$_.clone(this.form);
                data["message"] = Object.keys(data["message"], {}).reduce((acc, key) => {
                    if (data["message"][key] === "") data["message"][key] = "";
                    acc.push({ content: data["message"][key], code: key });
                    return acc;
                }, []);
                data["confirmAskingDialog"] = Object.keys(data["confirmAskingDialog"], {}).reduce((acc, key) => {
                    if (data["confirmAskingDialog"][key] === "") data["confirmAskingDialog"][key] = "";
                    acc.push({ content: data["confirmAskingDialog"][key], code: key });
                    return acc;
                }, []);
                data["dismissAskingDialog"] = Object.keys(data["dismissAskingDialog"], {}).reduce((acc, key) => {
                    if (data["dismissAskingDialog"][key] === "") data["dismissAskingDialog"][key] = "";
                    acc.push({ content: data["dismissAskingDialog"][key], code: key });
                    return acc;
                }, []);
                if (this.editMode) {
                    data["_id"] = this.editId;
                    await this.updateTrigger(data);
                } else {
                    data.order = this.trigger.length;
                    await this.addTrigger(data);
                }
                this.triggerInfo["afterUpdated"] = this.form;
                this.reset();
            } catch (e) {
            } finally {
                let updatedTrigger = this.$_.clone(this.triggerInfo.afterUpdated);
                delete updatedTrigger["updatedAt"];
                const messageOfUpdatedT = Object.values(updatedTrigger["message"] || {});
                delete updatedTrigger["message"];
                if (!!this.triggerInfo.beforeUpdated) {
                    let trigger = this.$_.clone(this.triggerInfo.beforeUpdated);
                    delete trigger["updatedAt"];
                    const messageOfT = trigger["message"].map((x) => x.content);
                    delete trigger["message"];
                    if (
                        !_.isEqual(trigger, updatedTrigger) ||
                        JSON.stringify(messageOfUpdatedT) !== JSON.stringify(messageOfT)
                    )
                        this.playPreview(this.triggerInfo.afterUpdated);
                }
                this.loading = false;
                await this.hideModalAsync();
            }
        },

        async hideModalAsync() {
            await this.$nextTick();
            this.hideModal();
        },

        onDeleteLanguage(data) {
            this.form.message = data.content;
        },
        getState(str) {
            return this[str];
        },
        addCondition() {
            if (this.form.conditions.length >= 5) {
                this.$message.error(this.$t("message.maxCondition"));
                return;
            }
            let newCondObj = this.$_.clone({
                condition: "dayTime",
                operator: "equal",
                value: "0",
            });
            this.$set(this.form.conditions, this.form.conditions.length, newCondObj);
        },
        delCondition(index) {
            this.form.conditions.splice(index, 1);
            // this.$set(this.form.conditions, index, null);
        },
        edit(item) {
            const id = item._id;
            let maybeData = this.$_.clone(this.trigger.find((item) => item._id === id));
            this.triggerInfo["beforeUpdated"] = _.cloneDeep(maybeData);
            if (!!maybeData) {
                this.editMode = true;
                this.editId = id;
                if (Array.isArray(maybeData["message"]))
                    maybeData["message"] = maybeData["message"].reduce((acc, cur) => {
                        acc[cur.code] = cur.content;
                        return acc;
                    }, {});
                if (Array.isArray(maybeData["confirmAskingDialog"]))
                    maybeData["confirmAskingDialog"] = maybeData["confirmAskingDialog"].reduce((acc, cur) => {
                        acc[cur.code] = cur.content;
                        return acc;
                    }, {});
                if (Array.isArray(maybeData["dismissAskingDialog"]))
                    maybeData["dismissAskingDialog"] = maybeData["dismissAskingDialog"].reduce((acc, cur) => {
                        acc[cur.code] = cur.content;
                        return acc;
                    }, {});
                this.form = maybeData;
                /*                this.$bvModal.show("trigger-form");*/
                this.showModal();
            }
        },
        playPreview(trigger) {
            const isSameTrigger = _.get(this.selectedTrigger, "_id") === trigger._id;
            log("playPreview", { trigger, isSameTrigger });
            this.selectedTrigger = isSameTrigger ? null : trigger;
        },
        remove(_id) {
            this.$dialog
                .confirm(this.$t("message.doYouWishToDeleteTrigger"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteTrigger({ _id });
                });
        },
        clearHtml(item) {
            let contentHtml = _.get(item, "message.0.content", "") || "";
            const el = document.createElement("div");
            el.innerHTML = contentHtml;
            const content = el.textContent || el.innerText || "";
            let firstLineOfContent = content
                .split("\n")
                .filter((x) => x.length > 0)
                .map((y) => y.replace("&nbsp;", " "))[0];
            return content.split("\n").filter((x) => x.length > 0).length > 1
                ? firstLineOfContent + "..."
                : firstLineOfContent || "";
            //return contentHtml.replace(/\<\/?.*?\>/gi, "\n");
        },
    },
    beforeMount() {
        if (this.checkPermission(PERMISSION_TYPES["trigers.view"].tr.name) === false) {
            return router.push("/denied");
        }
    },
    mounted() {
        this.getTrigger().then(() => {
            if (this.queryAction == "create") this.isModalShow = true;
        });
    },
    watch: {
        "form.popupTireggers": function(newVal) {
            if (newVal) {
                if (!this.form.options.includes('ask')) {
                    this.form.options.push('ask');
                }
            } else {
                const index = this.form.options.indexOf('ask');
                if (index !== -1) {
                    this.form.options.splice(index, 1);
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
#triggers {
    .trigger-recipe {
        display: flex;
        padding: 10px;
        justify-content: center;
        align-items: center;
        text-align: center;
        margin: 10px;
    }

    .slide-enter {
        opacity: 0;
    }

    .slide-enter-active {
        animation: slide-in 1s ease-out forwards;
        transition: opacity 1s;
    }

    .slide-leave {
    }

    .slide-leave-active {
        animation: slide-out 1s ease-out backwards;
        transition: opacity 1s;
        opacity: 0;
    }

    @keyframes slide-in {
        from {
            transform: translateX(20px);
        }
        to {
            transform: translateX(0px);
        }
    }
    @keyframes slide-out {
        from {
            transform: translateX(0px);
        }
        to {
            transform: translateX(20px);
        }
    }

    .trigger-recipe-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        width: 100%;
        height: 100%;
    }

    .preview-trigger {
        background: #ffffffbf;
        border-radius: 8px;
        color: #000000;
        margin: 8px 0px;
        box-shadow: 1px 2px 4px #00000070;
        cursor: pointer;
        max-height: 100%;
        padding: 6px 12px;
        max-width: 375px;
        font-weight: 200;
        user-select: none;
        overflow: hidden;
    }

    .trigger-card {
        border-radius: 5px;
        background: #fff;
        border: 1px solid #e2e2e2;
        padding: 10px;
        cursor: move;

        .header {
            text-align: center;
            background: #e2e2e2;
            padding: 5px;
            margin-bottom: 8px;
        }

        .trigger-content {
            border-radius: 2px;
            border: 1px solid #e2e2e291;
            text-align: center;
            background: #e2e2e291;
            padding: 10px 5px 10px 5px;
            margin-bottom: 8px;
            font-weight: 500;
        }

        .content {
            .content-item {
                display: flex;

                &:not(:last-child) {
                    margin-bottom: 5px;
                }

                .title {
                    margin-right: 3px;
                    color: #000000;
                }

                .description {
                    max-width: 90%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }
        }

        .separate-trigger-switch {
            margin: 0px 10px 3px;
            display: flex;
            flex-direction: row;
            justify-content: center;
            align-items: center;
            text-align: center;
        }
    }

    .fab {
        width: 50px;
        height: 50px;
        border-radius: 25px;
        background-color: white;
        position: absolute;
        bottom: 20px;
        left: 100px;
        z-index: 9999;
        transition: all 0.3s;
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
        transform: scale(1);
        animation: pulse 3s infinite;
        cursor: pointer;
    }

    @keyframes pulse {
        0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
        }

        70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px #00000000;
        }

        100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 #00000000;
        }
    }

    .trigger-labels {
        align-content: center !important;
        gap: 4px !important;
        grid-auto-flow: column !important;
        justify-content: center !important;
        place-items: center !important;
    }

    .skeletons {
        display: flex;
        flex-direction: column;
        margin-right: 30px;
        margin-left: 150px;
        padding-top: 75px;
    }

    .b-skeleton-img {
        width: 100%;
        height: 100%;
    }
}
</style>
<style lang="scss">
#trigger-form {
    #modal-inner {
        overflow-y: scroll;
        overflow-x: hidden;
        max-height: 70vh;
        padding: 0 20px;
    }

    .ant-modal-wrap {
        max-height: 100vh;
        /*overflow-y: hidden !important;*/
        /*top: -50px;*/
    }

    .ant-modal {
        width: 800px !important;
        top: 2rem;
    }

    #buttons {
        display: flex;
        gap: 7px;
        justify-content: flex-end;
        padding-right: 20px;
    }
}
</style>
